import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.string().uuid();
const JsonObjectSchema = z.record(z.string(), z.unknown());

const RunStatusSchema = z.enum(["queued", "running", "completed", "failed"]);
const VerdictSchema = z.enum(["pass", "fail", "error"]);

// ============================================================================
// Response Shapes
// ============================================================================

const SchemaEvalSuiteResponseSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  schemaDefinitionId: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  createdBy: UUIDSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  caseCount: z.number().int().nonnegative().optional(),
});

const SchemaEvalCaseResponseSchema = z.object({
  id: UUIDSchema,
  suiteId: UUIDSchema,
  documentId: UUIDSchema,
  documentFileName: z.string().nullable(),
  expectedExtraction: JsonObjectSchema,
  sourceOcrResultId: UUIDSchema.nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

const SchemaEvalRunSummarySchema = z.object({
  totalCases: z.number().int().nonnegative(),
  passed: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errored: z.number().int().nonnegative(),
  totalCostUsd: z.number().nullable(),
  regressions: z.number().int().nonnegative(),
  improvements: z.number().int().nonnegative(),
});

const SchemaEvalRunResponseSchema = z.object({
  id: UUIDSchema,
  suiteId: UUIDSchema,
  schemaDefinitionVersionId: UUIDSchema.nullable(),
  status: RunStatusSchema,
  summary: SchemaEvalRunSummarySchema.nullable(),
  previousRunId: UUIDSchema.nullable(),
  startedBy: UUIDSchema,
  startedByName: z.string().nullable(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
});

const FieldDiffSchema = z.object({
  expected: z.unknown(),
  actual: z.unknown(),
  kind: z.enum(["missing", "unexpected", "mismatch"]),
});

const SchemaEvalCaseResultResponseSchema = z.object({
  id: UUIDSchema,
  runId: UUIDSchema,
  caseId: UUIDSchema,
  verdict: VerdictSchema,
  // Expected pulled from the case at serialization time so the run-details
  // UI can render side-by-side actual/expected without a second fetch.
  expectedExtraction: JsonObjectSchema.nullable(),
  actualExtraction: JsonObjectSchema.nullable(),
  caseDocumentFileName: z.string().nullable(),
  diff: z.record(z.string(), FieldDiffSchema).nullable(),
  costUsd: z.number().nullable(),
  durationMs: z.number().int().nullable(),
  errorMessage: z.string().nullable(),
  createdAt: z.string(),
});

const HistoricalOcrRunSchema = z.object({
  ocrResultId: UUIDSchema,
  documentId: UUIDSchema,
  documentFileName: z.string(),
  ocrCreatedAt: z.string(),
  extractedResult: JsonObjectSchema.nullable(),
});

// ============================================================================
// Input Schemas
// ============================================================================

const ListSuitesInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
});

const CreateSuiteInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  name: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
});

const DeleteSuiteInputSchema = z.object({
  suiteId: UUIDSchema,
});

const ListCasesInputSchema = z.object({
  suiteId: UUIDSchema,
});

const CreateCaseInputSchema = z.object({
  suiteId: UUIDSchema,
  documentId: UUIDSchema,
  expectedExtraction: JsonObjectSchema,
  sourceOcrResultId: UUIDSchema.nullable().optional(),
  notes: z.string().nullable().optional(),
});

const UpdateCaseInputSchema = z.object({
  caseId: UUIDSchema,
  expectedExtraction: JsonObjectSchema.optional(),
  notes: z.string().nullable().optional(),
});

const DeleteCaseInputSchema = z.object({
  caseId: UUIDSchema,
});

const ListHistoricalRunsInputSchema = z.object({
  schemaDefinitionId: UUIDSchema,
  limit: z.number().int().min(1).max(200).optional(),
  offset: z.number().int().nonnegative().optional(),
});

const SeedFromHistoryInputSchema = z.object({
  suiteId: UUIDSchema,
  ocrResultIds: z.array(UUIDSchema).min(1),
});

const RunSuiteInputSchema = z.object({
  suiteId: UUIDSchema,
});

const ListRunsInputSchema = z.object({
  suiteId: UUIDSchema,
});

const GetRunInputSchema = z.object({
  runId: UUIDSchema,
});

// ============================================================================
// Contract
// ============================================================================

export const schemaEvalsContracts = {
  listSuites: oc
    .input(ListSuitesInputSchema)
    .output(z.array(SchemaEvalSuiteResponseSchema))
    .route({
      method: "GET",
      summary: "List extraction eval suites for a schema",
      description:
        "Returns the eval suites attached to one document extraction schema, with name, description, and case count.",
    }),

  createSuite: oc
    .input(CreateSuiteInputSchema)
    .output(SchemaEvalSuiteResponseSchema)
    .route({
      method: "POST",
      summary: "Create an extraction eval suite",
      description:
        "Creates a named eval suite for one document extraction schema, used to regression-test its extraction output against expected values.",
    }),

  deleteSuite: oc
    .input(DeleteSuiteInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "DELETE",
      summary: "Delete an extraction eval suite",
      description:
        "Permanently deletes an extraction eval suite from the organization.",
    }),

  listCases: oc
    .input(ListCasesInputSchema)
    .output(z.array(SchemaEvalCaseResponseSchema))
    .route({
      method: "GET",
      summary: "List test cases in an extraction eval suite",
      description:
        "Returns each case in a suite with its source document, expected extraction values, and notes.",
    }),

  createCase: oc
    .input(CreateCaseInputSchema)
    .output(SchemaEvalCaseResponseSchema)
    .route({
      method: "POST",
      summary: "Add an extraction eval test case",
      description:
        "Adds a document to a suite with the extraction values it should produce, plus optional notes and the source extraction result.",
    }),

  updateCase: oc
    .input(UpdateCaseInputSchema)
    .output(SchemaEvalCaseResponseSchema)
    .route({
      method: "PATCH",
      summary: "Update an extraction eval test case",
      description: "Edits a case's expected extraction values or notes.",
    }),

  deleteCase: oc
    .input(DeleteCaseInputSchema)
    .output(z.object({ success: z.boolean() }))
    .route({
      method: "DELETE",
      summary: "Delete an extraction eval test case",
      description:
        "Permanently removes one test case from its extraction eval suite.",
    }),

  listHistoricalRuns: oc
    .input(ListHistoricalRunsInputSchema)
    .output(
      z.object({
        items: z.array(HistoricalOcrRunSchema),
        total: z.number().int().nonnegative(),
      }),
    )
    .route({
      method: "GET",
      summary: "List past extractions for a schema",
      description:
        "Pages through completed document extractions produced by one schema (up to 200 per page), with each document's extracted result, for seeding eval cases.",
    }),

  seedFromHistory: oc
    .input(SeedFromHistoryInputSchema)
    .output(z.array(SchemaEvalCaseResponseSchema))
    .route({
      method: "POST",
      summary: "Create eval cases from past extractions",
      description:
        "Turns selected past extraction results into test cases in a suite, using each extracted result as the expected values. Returns the new cases.",
    }),

  runSuite: oc
    .input(RunSuiteInputSchema)
    .output(SchemaEvalRunResponseSchema)
    .route({
      method: "POST",
      summary: "Run an extraction eval suite",
      description:
        "Re-extracts every case's document with the schema's active version, compares to expected values, and records pass, fail, or error with regressions versus the last run.",
    }),

  listRuns: oc
    .input(ListRunsInputSchema)
    .output(z.array(SchemaEvalRunResponseSchema))
    .route({
      method: "GET",
      summary: "List runs of an extraction eval suite",
      description:
        "Returns a suite's eval runs with status, who started them, timing, and pass, fail, cost, and regression totals.",
    }),

  getRun: oc
    .input(GetRunInputSchema)
    .output(
      z.object({
        run: SchemaEvalRunResponseSchema,
        results: z.array(SchemaEvalCaseResultResponseSchema),
      }),
    )
    .route({
      method: "GET",
      summary: "Get an extraction eval run with results",
      description:
        "Returns one run plus per-case verdicts with expected and actual extraction values, field-level diffs, cost, and duration.",
    }),
} as const;

export type SchemaEvalSuiteResponse = z.infer<
  typeof SchemaEvalSuiteResponseSchema
>;
export type SchemaEvalCaseResponse = z.infer<
  typeof SchemaEvalCaseResponseSchema
>;
export type SchemaEvalRunResponse = z.infer<typeof SchemaEvalRunResponseSchema>;
export type SchemaEvalCaseResultResponse = z.infer<
  typeof SchemaEvalCaseResultResponseSchema
>;
export type HistoricalOcrRun = z.infer<typeof HistoricalOcrRunSchema>;
export type SchemaEvalRunSummaryResponse = z.infer<
  typeof SchemaEvalRunSummarySchema
>;
